import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';

import { CHECKLIST_TARGETING, selectFeature } from 'next/entities/features';
import { getLeafNodes } from 'next/lib/conditions';
import {
  selectProfileAttributeOptions,
  selectFlowTargetingOptions,
  selectEventTargetingOptions,
  selectSegmentTargetingOptions,
  selectChecklistTargetingOptions,
  selectGroupAttributeOptions,
  selectProfileAttributeSuggestions,
  selectGroupAttributeSuggestions,
} from 'next/lib/selectors-options';
import { AttributeSuggestionsShape, SelectOptionsShape } from 'next/lib/shapes';

import { ConditionsContext } from './ConditionsContext';
import { Group, CLAUSE_TYPES } from './Group';
import { LANGUAGE_OPTIONS } from './constants';

export const Conditions = ({
  propertyOptions = [],
  propertySuggestions = {},
  checklistOptions = [],
  eventOptions = [],
  flowOptions = [],
  groupOptions = [],
  groupSuggestions = {},
  hasChecklistTargeting,
  segmentOptions = [],
  validClauseTypes = Object.keys(CLAUSE_TYPES),
  ...restProps
}) => {
  const { conditions = { and: [] } } = restProps;

  const canTargetChecklists = useMemo(
    () =>
      getLeafNodes(conditions).some(clause => Boolean(clause.checklists)) ||
      hasChecklistTargeting,
    [conditions, hasChecklistTargeting]
  );

  const context = useMemo(
    () => ({
      canTargetChecklists,
      checklistOptions,
      eventOptions,
      flowOptions,
      groupOptions,
      groupSuggestions,
      hasChecklistTargeting,
      languageOptions: LANGUAGE_OPTIONS,
      propertyOptions,
      propertySuggestions,
      segmentOptions,
      validClauseTypes,
    }),
    [
      checklistOptions,
      eventOptions,
      flowOptions,
      groupOptions,
      groupSuggestions,
      hasChecklistTargeting,
      propertyOptions,
      propertySuggestions,
      segmentOptions,
      validClauseTypes,
    ]
  );

  return (
    <ConditionsContext.Provider value={context}>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <Group {...restProps} />
    </ConditionsContext.Provider>
  );
};

Conditions.propTypes = {
  checklistOptions: SelectOptionsShape,
  eventOptions: SelectOptionsShape,
  flowOptions: SelectOptionsShape,
  groupOptions: SelectOptionsShape,
  groupSuggestions: AttributeSuggestionsShape,
  hasChecklistTargeting: PropTypes.bool,
  propertyOptions: SelectOptionsShape,
  propertySuggestions: AttributeSuggestionsShape,
  segmentOptions: SelectOptionsShape,
  validClauseTypes: PropTypes.arrayOf(
    PropTypes.oneOf(Object.keys(CLAUSE_TYPES))
  ),
};

const mapStateToProps = state => ({
  hasChecklistTargeting: selectFeature(state, CHECKLIST_TARGETING),
  checklistOptions: selectChecklistTargetingOptions(state),
  eventOptions: selectEventTargetingOptions(state),
  flowOptions: selectFlowTargetingOptions(state),
  groupOptions: selectGroupAttributeOptions(state),
  groupSuggestions: selectGroupAttributeSuggestions(state),
  propertyOptions: selectProfileAttributeOptions(state),
  propertySuggestions: selectProfileAttributeSuggestions(state),
  segmentOptions: selectSegmentTargetingOptions(state),
});

export default styled(connect(mapStateToProps)(Conditions))``;
